






























































































































































































































































































































import Vue from 'vue';
import { Type, MapInput, EntityPropPair, Types, objectToArray, RelationKind, Relation, DefinitionsRelationReference } from 'expangine-runtime';
import { ListOptions, ListOption } from '../common';
import { sendNotification } from './Notify';
import { getConfirmation } from './Confirm';
import { TypeSettings } from '../runtime/types/TypeVisuals';
import { Registry } from '../runtime/Registry';
import { Preferences, PreferenceCategory } from './Preference';



export interface RelationData
{
  name: string;                   // hasMany hasOne
  one: string;                    // hasMany hasOne
  many: string;                   // hasMany
  oneRelationName: string;        // hasMany hasOne
  manyRelationName: string;       // hasMany
  foreignKeyPrefix: string;       // hasMany hasOne hasOnePolymorphic
  owns: boolean;                  // hasMany hasOne hasOnePolymorphic
  hasOne: string;                 // hasOne hasOnePolymorphic
  required: boolean;              // hasOne hasOnePolymorphic
  hasOneRelationName: string;     // hasOne hasOnePolymorphic
  morphs: EntityPropPair;         // hasOnePolymorphic
  morphsToRelated: MapInput<any, string>; // hasOnePolymorphic
  poly: string[];                 // hasOnePolymorphic
  polyRelationName: string;       // hasOnePolymorphic
}

const PREF_REMOVE_RELATION = Preferences.define({
  key: 'relation_remove',
  label: 'Remove relation without confirmation',
  category: [PreferenceCategory.CONFIRM],
  defaultValue: false,
});

export default Vue.extend({
  props: {
    relation: { 
      type: Object as () => Relation,
      required: true,
    },
    registry: {
      type: Object as () => Registry,
      required: true,
    },
    hideName: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    relationData: null as any as RelationData,
    kind: null as null | RelationKind,
    updatingData: false,
    tab: 0,
  }),
  computed: { 
    references(): DefinitionsRelationReference[] {
      return this.relation ? this.registry.defs.getRelationReferences(this.relation) : [];
    },
    kindOptions(): ListOptions<RelationKind> {
      return [
        { text: 'Has Many', value: RelationKind.HAS_MANY, description: 'One type is related to any number of instances of another type.' },
        { text: 'Has One', value: RelationKind.HAS_ONE, description: 'One type is related to one instance of another type.' },
        { text: 'Has One Polymorphic', value: RelationKind.HAS_ONE_POLYMORPHIC, description: 'One type is related to one instance of any number of types.' },
      ];
    },
    entityOptions(): ListOptions<string> {
      return this.registry.defs.entities.values
        .filter((entity) => entity.canStore(this.registry.defs))
        .map((entity) => ({
          text: entity.name,
          value: entity.name,
        }))
      ;
    },
    kindOption(): ListOption<RelationKind> | null { 
      return this.kindOptions.find((option) => option.value === this.kind) || null;
    },
    hintKind(): string {
      return this.kindOption
        ? this.kindOption.description || ''
        : '';
    },
    invalidName(): boolean {
      const name = this.relationData.name || this.relationName;

      if (!name) {
        return false;
      }
      const existing = this.registry.defs.relations.get(name);

      if ((this.relation && existing !== this.relation) || (!this.relation && existing)) {
        return true;
      }
      return false;
    },
    invalidKind(): boolean  {
      return this.kind === null;
    },
    isHasMany() {
      return this.kind === RelationKind.HAS_MANY;
    },
    isHasOne() {
      return this.kind === RelationKind.HAS_ONE;
    },
    isHasOnePolymorphic() {
      return this.kind === RelationKind.HAS_ONE_POLYMORPHIC;
    },
    invalidHasManyRelation(): boolean {
      const { one, many } = this.relationData;

      return !one || !many;
    },
    invalidHasOneRelation(): boolean {
      const { hasOne, one } = this.relationData;

      return !one || !hasOne;
    },
    invalidHasOnePolymorphicRelation(): boolean {
      const { hasOne, morphs, poly, hasOneRelationName } = this.relationData;

      return !hasOne || !morphs || !morphs[0] || !morphs[1] || poly.length < 2 || !hasOneRelationName;
    },
    invalidOne(): boolean {
      return !this.relationData.one;
    },
    invalidMany(): boolean {
      return !this.relationData.many;
    },
    invalidHasOne(): boolean {
      return !this.relationData.hasOne;
    },
    invalidHasOneRelationName(): boolean {
      return !this.relationData.hasOneRelationName;
    },
    invalidPoly(): boolean {
      return this.relationData.poly.length < 2;
    },
    relationName(): string {
      const { kind, relationData } = this;
      const { one, many, hasOne, oneRelationName, manyRelationName, hasOneRelationName, polyRelationName } = relationData;
      let parts = ['', '', ''];

      switch (kind) {
        case RelationKind.HAS_MANY:
          parts = [manyRelationName || one, 'hasMany', oneRelationName || many];
          break;
        case RelationKind.HAS_ONE:
          parts = [hasOneRelationName || one, 'hasOne', oneRelationName || hasOne];
          break;
        case RelationKind.HAS_ONE_POLYMORPHIC:
          parts = [hasOneRelationName, '_hasOnePolymorphic_', polyRelationName || hasOne];
          break;
      }

      return parts[0] && parts[2]
        ? parts.join('_')
        : '';
    },
    hintName(): string {
      const name = this.relationData.name || this.relationName;

      return this.invalidName
        ? `The name "${name}" is already in use`
        : this.relationName
          ? `The name defaults to "${this.relationName}"`
          : `The name will be auto-populated if you don't enter anything`;
    },
    hintHasManyOne(): string {
      const { many, oneRelationName } = this.relationData;

      return many && !oneRelationName
        ? `Defaults to "${many}"`
        : 'The name of the relation on the One type';
    },
    hintHasOneHasOne(): string {
      const { one, hasOneRelationName } = this.relationData;

      return one && !hasOneRelationName
        ? `Defaults to "${one}"`
        : 'The name of the relation on the Has One type';
    },
    hintHasOnePolymorphicHasOne(): string {
      return 'The name of the relation on the Has One type';
    },
    hintHasOnePolymorphicOne(): string {
      const { hasOne, polyRelationName } = this.relationData;

      return hasOne && !polyRelationName
        ? `Defaults to "${hasOne}"`
        : 'The name of the relation on the One types';
    },
    hintHasOneOne(): string {
      const { hasOne, oneRelationName } = this.relationData;

      return hasOne && !oneRelationName
        ? `Defaults to "${hasOne}"`
        : 'The name of the relation on the One type';
    },
    hintHasManyMany(): string {
      const { one, manyRelationName } = this.relationData;

      return one && !manyRelationName
        ? `Defaults to "${one}"`
        : 'The name of the relation on the Many type';
    },
    morphsTypeOptions(): ListOptions<Type> {
      return [
        { text: 'Text', value: Types.text() },
        { text: 'Number', value: Types.number() },
        { text: 'Whole Number', value: Types.int() },
        { text: 'Boolean', value: Types.bool() },
        { text: 'Enum', value: Types.enumForText(this.relationData.poly) },
      ];
    },
    morphsPropertyOptions(): ListOptions<string> {
      const hasOne = this.registry.defs.getEntity(this.relationData.hasOne);

      if (!hasOne) {
        return [];
      }

      return objectToArray(hasOne.type.options.props, (_, value) => ({
        value,
        text: value,
      }));
    },
    morphsTypeSettings(): TypeSettings {
      return this.registry.getTypeSettings(this.relationData.morphs[1]);
    },
    disableSave(): boolean {
      return this.invalidName 
        || this.kind === null
        || (this.isHasMany && this.invalidHasManyRelation)
        || (this.isHasOne && this.invalidHasOneRelation)
        || (this.isHasOnePolymorphic && this.invalidHasOnePolymorphicRelation);
    },
  },
  watch: {
    relation: {
      immediate: true,
      handler(relation: Relation) {
        if (this.updatingData) {
          this.updatingData = false;
        } else {
          this.relationData = this.getRelationData();
          this.kind = relation.kind;
        }
      },
    },
    relationData: {
      deep: true,
      handler(relationData: RelationData) {
        const { registry: { defs }, kind } = this;

        if (!this.disableSave && kind !== null) {
          const relation = this.getRelationFromData();

          if (relation) {
            this.updatingData = true;
            defs.addRelation(relation);

            this.$emit('create', relation);
          }
        }
      },
    },
  },
  methods: {
    renamed(newName: string) {
      const { registry, relation } = this;
      const { defs } = registry;

      if (relation) {
        if (relation.name) {
          const updates = defs.renameRelation(relation, newName);

          if (updates && updates.length) {
            sendNotification({ message: `${updates.length} Function reference(s) updated.` });
          }
        } else {
          relation.name = newName;

          defs.addRelation(relation);
        }
      }
    },
    validateName(name: string) {
      if (!name) {
        return 'A name is required.';
      }

      const existing = this.registry.defs.getRelation(name);
      if (existing && existing !== this.relation) {
        return 'A relation already exists with that name.';
      }

      return '';
    },
    async remove() {
      const refs = this.references.length;

      if (!await getConfirmation({ message: `Are you sure you want to remove this function? It is referenced ${refs} times.`, pref: PREF_REMOVE_RELATION })) {
        return;
      }

      if (!this.registry.defs.removeRelation(this.relation)) {
        sendNotification({ message: 'You cannot remove a referenced function. '});
      } else {
        this.$emit('remove', this.relation);
      }
    },
    morphsTypesComparator(a: Type, b: Type) {
      return a.getId() === b.getId();
    },
    getRelationFromData() {
      switch (this.kind) {
        case RelationKind.HAS_MANY:
          return Relation.hasMany(this.registry.defs, this.relationData);
        case RelationKind.HAS_ONE:
          return Relation.hasOne(this.registry.defs, this.relationData);
        case RelationKind.HAS_ONE_POLYMORPHIC:
          return Relation.hasOnePolymorphic(this.registry.defs, this.relationData);
      }
    },
    getRelationDataDefaults(): RelationData {
      return {
        name: '',
        one: '',
        many: '',
        oneRelationName: '',
        manyRelationName: '',
        foreignKeyPrefix: '',
        owns: false,
        hasOne: '',
        required: false,
        hasOneRelationName: '',
        morphs: ['discriminator', Types.text()],
        morphsToRelated: {},
        poly: [],
        polyRelationName: '',
      };
    },
    getRelationData() {
      const relation = this.relation;
      const data = this.getRelationDataDefaults();

      if (!relation)
      {
        return data;
      }

      switch (relation.kind)
      {
        case RelationKind.HAS_MANY:
          data.name = relation.name;
          data.one = relation.related[0].name;
          data.oneRelationName = relation.relatedRelationName;
          data.many = relation.subject.name;
          data.manyRelationName = relation.subjectRelationName;
          data.foreignKeyPrefix = relation.subjectRelationName + '_';
          data.owns = relation.owns;
          break;

        case RelationKind.HAS_ONE:
          data.name = relation.name;
          data.hasOne = relation.subject.name;
          data.hasOneRelationName = relation.subjectRelationName;
          data.one = relation.related[0].name;
          data.oneRelationName = relation.relatedRelationName;
          data.foreignKeyPrefix = relation.subjectRelationName + '_';
          data.required = relation.required;
          data.owns = relation.owns;
          break;

        case RelationKind.HAS_ONE_POLYMORPHIC:
          const map = Array.from(relation.morphsToRelated.entries());

          data.name = relation.name;
          data.hasOne = relation.subject.name;
          data.hasOneRelationName = relation.subjectRelationName;
          data.poly = relation.related.map((related) => related.name);
          data.polyRelationName = relation.relatedRelationName;
          data.morphs = relation.morphs as EntityPropPair;
          data.morphsToRelated = map.reduce((obj, [prop, value]) => (obj[prop] = value, obj), {} as Record<string, any>);
          data.foreignKeyPrefix = relation.subjectRelationName + '_';
          data.required = relation.required;
          data.owns = relation.owns;
          break;
      }

      return data;
    },
  },
});
